import type { DashboardMenuItemContent } from "./DashboardMenuItem";

import * as React from "react";

import { DashboardSidebarSeparator } from "@editor/components/shops-ui/DashboardSidebarSeperator";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLocalStorage } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { storeToken } from "@editor/reducers/utils/store-token";
import { authRouter } from "@editor/trpc/auth";
import { routes } from "@editor/utils/router";

import { DashboardMenuItems } from "@/editor/components/shops-ui/DashboardMenuItems";
import AnnouncementsMenu from "@/features/announcements/AnnouncementsMenu";
import IconButton from "@replo/design-system/components/button/IconButton";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "@replo/design-system/components/shadcn/core/sidebar";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  ArrowUpRight,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileText,
  GraduationCap,
  MessageSquareShare,
  ThumbsUp,
  Upload,
  User,
  UserCheck,
} from "lucide-react";
import { generatePath, useLocation, useNavigate } from "react-router-dom";

import DashboardMenuItem from "./DashboardMenuItem";
import ShopSelector from "./shop-selector/ShopSelector";

const getResourcesLinks = (hostingType: string): DashboardMenuItemContent[] => {
  const reploLinks: DashboardMenuItemContent[] = [
    {
      title: "Help Docs",
      href: "https://docs.replo.app",
      startEnhancer: <FileText size={16} />,
      endEnhancer: <ArrowUpRight size={16} />,
    },
    {
      title: "Join the Discord",
      href: "https://discord.gg/E7K6VADQJM",
      startEnhancer: <MessageSquareShare size={16} />,
      endEnhancer: <ArrowUpRight size={16} />,
    },
    {
      title: "Feedback",
      href: "https://feedback.replo.app/?utm_source=dashboard",
      startEnhancer: <ThumbsUp size={16} />,
      endEnhancer: <ArrowUpRight size={16} />,
    },
  ];

  const shopifyLinks: DashboardMenuItemContent[] = [
    {
      title: "Reploversity",
      href: "https://www.replo.app/learn",
      startEnhancer: <GraduationCap size={16} />,
      endEnhancer: <ArrowUpRight size={16} />,
    },
    {
      title: "Help Docs",
      href: "https://support.replo.app/",
      startEnhancer: <FileText size={16} />,
      endEnhancer: <ArrowUpRight size={16} />,
    },
    {
      title: "Find a Replo Expert",
      href: "https://contra.com/replo",
      startEnhancer: <UserCheck size={16} />,
      endEnhancer: <ArrowUpRight size={16} />,
    },
    {
      title: "Join the Discord",
      href: "https://discord.gg/E7K6VADQJM",
      startEnhancer: <MessageSquareShare size={16} />,
      endEnhancer: <ArrowUpRight size={16} />,
    },
    {
      title: "Feedback",
      href: "https://feedback.replo.app/?utm_source=dashboard",
      startEnhancer: <ThumbsUp size={16} />,
      endEnhancer: <ArrowUpRight size={16} />,
    },
  ];

  return hostingType === "replo" ? reploLinks : shopifyLinks;
};

const DashboardSidebar = () => {
  const location = useLocation();
  const { from } = location.state ?? {};
  const isAllProjectsRoute = location.pathname.startsWith(routes.allProjects);
  const shouldHideItems = isAllProjectsRoute || from === "allProjects";
  const logEvent = useLogAnalytics();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { project } = useCurrentProjectContext();
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();
  const localStorage = useLocalStorage();
  const [isShopSelectorOpen, setIsShopSelectorOpen] = React.useState(false);

  const resourcesLinks = getResourcesLinks(project?.hostingType ?? "replo");

  const { mutateAsync: logout } = authRouter.logout.useMutation({
    onSuccess: async () => {
      storeToken(null);
      window.location.replace("/auth/login");
    },
  });
  const { toggleSidebar, open, openMobile } = useSidebar();
  const isMobile = useIsMobile();
  const isSidebarExpanded = isMobile ? openMobile : open;

  const userItem = {
    title: user?.email ?? "",
    className: "text-default",
    startEnhancer: <User size={16} className="shrink-0" />,
    endEnhancer: <ChevronRight size={16} className="shrink-0" />,
    menuItems: [
      {
        type: "leaf" as const,
        id: "my-account",
        title: "My Account",
        variant: "default" as const,
        size: "sm" as const,
        startEnhancer: <User size={16} />,
        onSelect: () => {
          logEvent("dashboard.nav.click", {
            tab: "your_profile",
            userId: user?.id ?? null,
            workspaceId: workspaceId ?? null,
          });
          navigate(
            generatePath(routes.shops.settings.profile, {
              projectId,
            }),
          );
        },
      },
      {
        // NOTE (Sebas, 2024-12-04): We need this to be a section to add
        // the separator between the two items.
        type: "section" as const,
        items: [
          {
            type: "leaf" as const,
            id: "logout",
            title: "Logout",
            variant: "danger" as const,
            size: "sm" as const,
            startEnhancer: <Upload className="rotate-90" size={16} />,
            onSelect: () => {
              logEvent("dashboard.logout", {});
              void logout({});

              // Note (Noah, 2025-02-22): Make sure to clear the last visited
              // workspace when logging out, otherwise when you log in again to
              // a new account you'll see a permissions error by default
              localStorage.removeItem("replo.workspace.lastvisitedWorkspaceId");
            },
          },
        ],
      },
    ],
  };

  return (
    // NOTE (Max, 2025-04-09): We're just hiding the sidebar on mobile rather adapting it to mobile, as only a small
    // amount of users will ever go to the dashboard on mobile.
    <Sidebar
      variant="floating"
      className="list-none p-3 pr-4 z-15"
      collapsible="icon"
    >
      <SidebarHeader className="relative p-3">
        <SidebarMenu>
          <SidebarMenuItem>
            {/* NOTE (Ben, 2025-09-11): Adding a spacer div to maintain the layout where the ShopSelectorCard would be
             * Doing this so we can have a sexy morphing menu.
             */}
            <div className="px-1.5 py-1 h-10" />
            <div
              className={twMerge(
                "absolute top-[-12px] left-[-12px] z-1 w-[calc(100%+24px)]",
                isShopSelectorOpen && "z-10",
              )}
            >
              <ShopSelector
                isSidebarExpanded={isSidebarExpanded}
                setIsShopSelectorOpen={setIsShopSelectorOpen}
              />
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <div className="relative">
        <div className="absolute top-[-12px] right-[-12px] z-1">
          <IconButton
            size="sm"
            variant="secondary"
            icon={
              isSidebarExpanded ? (
                <ChevronsLeft size={12} />
              ) : (
                <ChevronsRight size={12} />
              )
            }
            onClick={() => {
              toggleSidebar();
            }}
          />
        </div>

        <DashboardSidebarSeparator insetRight className="mx-3" />
      </div>

      <SidebarContent className="flex flex-col justify-between pb-1 items-start list-none">
        {!shouldHideItems && <DashboardMenuItems />}
      </SidebarContent>
      <SidebarFooter className="p-3 py-4">
        <div
          className={twMerge(
            "flex flex-col gap-2",
            !isSidebarExpanded && "ml-1",
          )}
        >
          <AnnouncementsMenu source="dashboard" items={resourcesLinks} />
          <DashboardMenuItem item={userItem} nestedMenuAlignOffset={-16} />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default DashboardSidebar;
